import type {
  ArticulationAgreement,
  ArticulationSubject,
  ExternalCourse,
  ExternalSubject,
  MatchingRequisite,
  SubjectRequest,
  SubjectSearchItem,
} from './api'
import { subjectDisplayCode } from './display'

export type PrecedentOutcome = SubjectSearchItem & {
  priority?: number
  rule_priority?: number
  title: string
  ruleGroupIndex: number
}

export type ArticulationCondition = {
  index: number
  text: string
}

export type ArticulationTableRow = {
  key: string
  agreementReference: string
  agreementRowspan: number
  showAgreementReference: boolean
  groupRowspan: number
  showGroupLabel: boolean
  subject: ArticulationSubject
  conditionIndex?: number
}

type SubjectPrecedentTableRowBase = {
  key: string
  showSubjectCode: boolean
  subjectRowspan: number
}

export type SubjectPrecedentOutcomeRow = SubjectPrecedentTableRowBase & {
  kind: 'outcome'
  outcome: PrecedentOutcome
  displaySubjectName: string
}

export type SubjectPrecedentManualRequestRow = SubjectPrecedentTableRowBase & {
  kind: 'manual-request'
  subjectRequest: SubjectRequest
  displaySubjectName: string
}

export type SubjectPrecedentEmptyRow = SubjectPrecedentTableRowBase & {
  kind: 'empty'
}

export type SubjectPrecedentButtonRow = SubjectPrecedentTableRowBase & {
  kind: 'request-button'
}

export type SubjectPrecedentTableRow =
  | SubjectPrecedentOutcomeRow
  | SubjectPrecedentManualRequestRow
  | SubjectPrecedentEmptyRow
  | SubjectPrecedentButtonRow

export type CourseRuleOutcomeRow = PrecedentOutcome & {
  displaySubjectName: string
}

export function outcomesFromMatchingRequisites(
  matchingRequisites: MatchingRequisite[],
  subjectYearLevel: number,
): PrecedentOutcome[] {
  const seenSubjectCodes = new Set<string>()
  const outcomes: PrecedentOutcome[] = []
  let ruleGroupIndex = 0

  for (const requisite of matchingRequisites.filter((item) => item.rule.filtered_outcomes.length > 0)) {
    let addedOutcomeForRule = false
    for (const outcome of requisite.rule.filtered_outcomes) {
      if (seenSubjectCodes.has(outcome.subject_code)) continue

      seenSubjectCodes.add(outcome.subject_code)
      addedOutcomeForRule = true
      outcomes.push({
        subject_code: outcome.subject_code,
        subject_name: outcome.subject_name,
        subject_year_level: subjectYearLevel,
        priority: outcome.priority,
        rule_priority: outcome.rule_priority,
        title: requisite.rule_id == null ? requisite.rule.rule_type ?? 'Default Rule' : `Rule ID: ${requisite.rule_id}`,
        ruleGroupIndex,
      })
    }

    if (addedOutcomeForRule) {
      ruleGroupIndex += 1
    }
  }

  return outcomes
}

export function precedentOutcomesForSubject(subject: ExternalSubject): PrecedentOutcome[] {
  return outcomesFromMatchingRequisites(subject.matching_requisites, Number(subject.external_subject_level))
}

export function courseRuleOutcomes(course: ExternalCourse): PrecedentOutcome[] {
  return outcomesFromMatchingRequisites(course.matching_requisites, 0)
}

export function courseRuleOutcomeRows(course: ExternalCourse): CourseRuleOutcomeRow[] {
  const outcomes = courseRuleOutcomes(course)

  return outcomes.map((outcome, index) => ({
    ...outcome,
    displaySubjectName: index + 1 < outcomes.length ? `${outcome.subject_name}, and` : outcome.subject_name,
  }))
}

export function manualSubjectRequestsForSubject(subject: ExternalSubject): SubjectRequest[] {
  const outcomeSubjectCodes = new Set(precedentOutcomesForSubject(subject).map((outcome) => outcome.subject_code))

  return subject.subject_request_list.filter((request) => {
    return !outcomeSubjectCodes.has(request.subject_code)
  })
}

export function subjectPrecedentRows(subject: ExternalSubject): SubjectPrecedentTableRow[] {
  const outcomes = precedentOutcomesForSubject(subject)
  const manualRequests = manualSubjectRequestsForSubject(subject)
  const outcomeDisplayNames = precedentOutcomeDisplayNames(outcomes, manualRequests.length)
  const manualRequestDisplayNames = manualSubjectRequestDisplayNames(manualRequests)
  const contentRowCount = outcomes.length + manualRequests.length
  const subjectRowspan = Math.max(2, contentRowCount + 1)
  const subjectKey = subject.external_subject_id ?? subjectDisplayCode(subject)
  const rows: SubjectPrecedentTableRow[] = []

  if (contentRowCount === 0) {
    rows.push({
      key: `${subjectKey}-empty`,
      kind: 'empty',
      showSubjectCode: true,
      subjectRowspan,
    })
  } else {
    outcomes.forEach((outcome, index) => {
      rows.push({
        key: `${subjectKey}-outcome-${outcome.subject_code}-${index}`,
        kind: 'outcome',
        outcome,
        displaySubjectName: outcomeDisplayNames[index] ?? '',
        showSubjectCode: rows.length === 0,
        subjectRowspan,
      })
    })

    manualRequests.forEach((subjectRequest, index) => {
      rows.push({
        key: `${subjectKey}-manual-${subjectRequest.subject_code}-${index}`,
        kind: 'manual-request',
        subjectRequest,
        displaySubjectName: manualRequestDisplayNames[index] ?? '',
        showSubjectCode: rows.length === 0,
        subjectRowspan,
      })
    })
  }

  rows.push({
    key: `${subjectKey}-request-button`,
    kind: 'request-button',
    showSubjectCode: false,
    subjectRowspan,
  })

  return rows
}

export function precedentOutcomeDisplayNames(outcomes: PrecedentOutcome[], manualRequestCount: number): string[] {
  const displayNames = outcomes.map((outcome) => outcome.subject_name)

  for (let index = 1; index < outcomes.length; index += 1) {
    const previousOutcome = outcomes[index - 1]
    const currentOutcome = outcomes[index]
    if (previousOutcome === undefined || currentOutcome === undefined) continue

    displayNames[index - 1] += previousOutcome.ruleGroupIndex === currentOutcome.ruleGroupIndex ? ' and' : ', or'
  }

  if (displayNames.length > 0 && manualRequestCount > 0) {
    displayNames[displayNames.length - 1] += ', or'
  }

  return displayNames
}

export function manualSubjectRequestDisplayNames(manualRequests: SubjectRequest[]): string[] {
  return manualRequests.map((request, index) => {
    const subjectName = request.subject_name ?? ''
    return index + 1 < manualRequests.length ? `${subjectName}, or` : subjectName
  })
}

export function subjectTriggersLegacyUnspecifiedWarning(subject: ExternalSubject): boolean {
  if (subject.external_subject_system_managed) return false
  if (manualSubjectRequestsForSubject(subject).length > 0) return false

  return precedentOutcomesForSubject(subject).some((outcome) => {
    return outcome.subject_name.startsWith('UNSPECIFIED CREDIT')
  })
}

export function outcomePriorityClass(outcome: { priority?: number; rule_priority?: number }): string {
  return `Priority_${outcome.priority ?? outcome.rule_priority ?? ''}`
}

export function articulationRows(course: ExternalCourse): ArticulationTableRow[] {
  const rows: ArticulationTableRow[] = []
  let conditionIndex = 0

  for (const agreement of course.articulation_agreements) {
    const agreementRows: ArticulationTableRow[] = []
    const agreementReference = articulationReference(agreement)

    for (const section of agreement.articulation_sections) {
      const sectionConditionIndex = section.conditions ? ++conditionIndex : undefined

      for (const group of section.subject_groups) {
        const subjects = group.subjects.filter((subject) => subject.subject_code)
        const groupRowspan = subjects.length
        if (groupRowspan === 0) continue

        for (const [subjectIndex, subject] of subjects.entries()) {
          agreementRows.push({
            key: `${agreementReference}-${rows.length}-${agreementRows.length}-${subject.subject_code}`,
            agreementReference,
            agreementRowspan: 0,
            showAgreementReference: false,
            groupRowspan,
            showGroupLabel: subjectIndex === 0,
            subject,
            conditionIndex: sectionConditionIndex,
          })
        }
      }
    }

    if (agreementRows.length > 0) {
      const firstAgreementRow = agreementRows[0]
      if (firstAgreementRow !== undefined) {
        firstAgreementRow.showAgreementReference = true
        firstAgreementRow.agreementRowspan = agreementRows.length
      }
      rows.push(...agreementRows)
    }
  }

  return rows
}

export function articulationReference(agreement: ArticulationAgreement): string {
  return agreement.reference ?? agreement.articulation_reference ?? ''
}

export function articulationConditions(course: ExternalCourse): ArticulationCondition[] {
  const conditions: ArticulationCondition[] = []

  for (const agreement of course.articulation_agreements) {
    for (const section of agreement.articulation_sections) {
      if (!section.conditions) continue

      conditions.push({
        index: conditions.length + 1,
        text: section.conditions,
      })
    }
  }

  return conditions
}
